import consent from "./consent";

/**
 *
 */
class ReactAndShare {
  dom: {
    root: HTMLElement;
    rnsContainer: HTMLElement;
    noConsentNotice: HTMLElement;
    rememberConsent?: HTMLInputElement;
    consentBtn: HTMLButtonElement;
    revokeConsentBtn: HTMLButtonElement;
  };

  loading: boolean = false;
  scriptLoaded: boolean = false;
  #consentPersistType: 'local' | 'session' = 'session';
  #consentType: 'cookie' | 'local' | 'session' | null = null;

  constructor(element: HTMLElement) {
    this.dom = {
      root: element,
      rnsContainer: element.querySelector('.rns') as HTMLElement,
      noConsentNotice: element.querySelector('[data-rns-no-consent]') as HTMLElement,
      rememberConsent: element.querySelector('[data-rns-persist]') as HTMLInputElement,
      consentBtn: element.querySelector('[data-rns-consent-btn]') as HTMLButtonElement,
      revokeConsentBtn: element.querySelector('[data-rns-revoke-consent]') as HTMLButtonElement,
    };

    this.init();
  }

  async init() {
    console.log('react-and-share.init()');

    consent.addEventListener('change', this.onConsentChange.bind(this));
    this.onConsentChange();

    if(this.dom.rememberConsent) {
      this.dom.rememberConsent.addEventListener('change', this.onRememberConsentChange.bind(this));
    }

    this.dom.consentBtn.addEventListener('click', this.onConsent.bind(this));

    this.dom.revokeConsentBtn.addEventListener('click', this.onConsentRevoked.bind(this));
  }

  getConsent() {
    const currentConsent = consent.getConsent().rns;
    this.#consentType = currentConsent;
    return currentConsent;
  }

  /**
   * Saves the consent to local storage if the checkbox is checked. This persists the consent across sessions.
   * If the checkbox is unchecked, it saves to session storage, which means the consent will be lost when the session ends.
   * @param event - Change event
   */
  onRememberConsentChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const checked = target.checked;

    this.#consentPersistType = checked ? 'local' : 'session';
  }

  /**
   * Handles consent button click
   *
   * @param event - Click event
   */
  onConsent = (event: Event) => {
    consent.setConsent({
      rns: this.#consentPersistType
    });
    consent.updateConsent();
  };

  /**
   * Handles revoke consent button click
   *
   * @param event - Click event
   */
  onConsentRevoked = (event: Event) => {
    consent.setConsent({
      rns: null,
    });
    consent.updateConsent();
  }

  /**
   * Listens to consent changes.
   * Consent can come from multiple sources, so we need to listen to changes.
   * @param event
   */
  onConsentChange = (event?: Event) => {
    const currentConsent = this.getConsent();
    // console.log('[RNS] Consent changed:', currentConsent);

    if(currentConsent) {
      if (!this.scriptLoaded) {
        this.dom.noConsentNotice.classList.add('loading');
        this.loadScript()
          .then(() => {
            this.scriptLoaded = true;
            this.showRNS();
          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        this.showRNS();
      }
    } else {
      this.hideRNS();
    }
  }

  showRNS() {
    this.dom.noConsentNotice.classList.remove('loading');
    this.dom.noConsentNotice.classList.add('hidden');
    this.dom.rnsContainer.classList.remove('hidden');

    if(['local', 'session'].includes(this.#consentType as string)) {
      this.dom.revokeConsentBtn.classList.remove('hidden');
    } else {
      this.dom.revokeConsentBtn.classList.add('hidden');
    }
  }

  hideRNS() {
    this.dom.noConsentNotice.classList.remove('hidden');
    this.dom.revokeConsentBtn.classList.add('hidden');
    // Tracking has already happened, on next page load the script is not loaded.
    // Probably enough to just hide the container.
    this.dom.rnsContainer.classList.add('hidden');
  }

  async loadScript() {
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://cdn.reactandshare.com/plugin/rns.js';

      script.onload = () => {
        // console.log(`[RNS] Script loaded successfully.`);
        resolve();
      };

      script.onerror = () => {
        console.error(`[RNS] Failed to load script.`);
        reject(new Error(`Failed to load script`));
      };

      document.head.appendChild(script);
    });
  }
}

export default ReactAndShare;
